import type { BodyLandmark, CameraPlacement, VisionResult } from "@studystraight/shared-types";
import {
  distance,
  midpoint,
  signedAngleFromHorizontal,
  signedAngleFromVertical,
} from "@studystraight/vision-core";

/**
 * Số đo thô cho Debug Mode — Prompt.md §25.
 *
 * Cố ý **không** phụ thuộc baseline hay `posture-core`: bước này đứng TRƯỚC engine (§34 Phase 4)
 * và mục đích của nó là cung cấp dữ liệu để chốt 4 Design Uncertainty trước khi engine được viết.
 * Nếu chờ engine xong mới có Debug Mode, ta sẽ tune threshold bằng cảm giác.
 */
export interface DebugSample {
  /** ms — timestamp của frame (không phải giờ hệ thống), để replay được. */
  t: number;
  confidence: number;
  aspectRatio: number;

  /** Góc tai↔vai so với trục dọc (DU #4 — bản 2D). */
  headAngle2D: number;
  /** Cùng đại lượng nhưng suy từ `worldLandmarks` (DU #4 — bản pitch 3D). */
  headAnglePitchWorld: number | null;
  /** Góc vai↔hông so với trục dọc. NaN khi không thấy hông (DU #3). */
  torsoAngle2D: number;
  /** Độ lệch vai so với trục ngang. */
  shoulderTilt: number;

  /** DU #2 — ba đại lượng ứng cử cho `distanceRatio`, log song song để so sánh. */
  earDistance: number;
  shoulderWidth: number;
  shoulderEarRatio: number;

  /** DU #3 — visibility của hông từng frame; tổng hợp thành tỉ lệ 60s ở HUD. */
  leftHipVisibility: number;
  rightHipVisibility: number;

  /** DU #1 — thời gian trọn vòng detect(). */
  inferenceMs: number;
}

/**
 * Ép cặp (y, z) của world landmark vào một điểm 2D để tái dùng `signedAngleFromVertical`:
 * "x" ở đây là **độ sâu** z. Đây là góc pitch của đầu trong mặt phẳng dọc — đại lượng mà camera
 * chính diện không đo được từ toạ độ 2D, và là lý do DU #4 tồn tại.
 */
function toDepthPlane(lm: BodyLandmark): BodyLandmark | null {
  if (lm.z === undefined) return null;
  return { x: lm.z, y: lm.y };
}

function headPitchFromWorld(vision: VisionResult): number | null {
  const world = vision.worldLandmarks;
  if (!world) return null;
  const { leftEar, rightEar, leftShoulder, rightShoulder } = world;
  if (!leftEar || !rightEar || !leftShoulder || !rightShoulder) return null;

  const ear = toDepthPlane(midpoint(leftEar, rightEar));
  const shoulder = toDepthPlane(midpoint(leftShoulder, rightShoulder));
  if (!ear || !shoulder) return null;
  // aspectRatio = 1: world landmark đã ở đơn vị mét, không phải toạ độ chuẩn hoá theo frame.
  return signedAngleFromVertical(ear, shoulder, 1);
}

export function computeDebugSample(
  vision: VisionResult,
  inferenceMs: number,
): DebugSample {
  const { landmarks, frameAspectRatio } = vision;
  const { leftEar, rightEar, leftShoulder, rightShoulder, leftHip, rightHip } = landmarks;

  const earMid = leftEar && rightEar ? midpoint(leftEar, rightEar) : null;
  const shoulderMid = leftShoulder && rightShoulder ? midpoint(leftShoulder, rightShoulder) : null;
  const hipMid = leftHip && rightHip ? midpoint(leftHip, rightHip) : null;

  const earDistance =
    leftEar && rightEar ? distance(leftEar, rightEar, frameAspectRatio) : Number.NaN;
  const shoulderWidth =
    leftShoulder && rightShoulder
      ? distance(leftShoulder, rightShoulder, frameAspectRatio)
      : Number.NaN;

  return {
    t: vision.timestamp,
    confidence: vision.confidence,
    aspectRatio: frameAspectRatio,
    headAngle2D:
      earMid && shoulderMid ? signedAngleFromVertical(earMid, shoulderMid, frameAspectRatio) : Number.NaN,
    headAnglePitchWorld: headPitchFromWorld(vision),
    torsoAngle2D:
      shoulderMid && hipMid ? signedAngleFromVertical(shoulderMid, hipMid, frameAspectRatio) : Number.NaN,
    shoulderTilt:
      leftShoulder && rightShoulder
        ? signedAngleFromHorizontal(leftShoulder, rightShoulder, frameAspectRatio)
        : Number.NaN,
    earDistance,
    shoulderWidth,
    shoulderEarRatio: shoulderWidth / earDistance,
    leftHipVisibility: leftHip?.visibility ?? 0,
    rightHipVisibility: rightHip?.visibility ?? 0,
    inferenceMs,
  };
}

/** DU #3 — tỉ lệ frame thấy được **cả hai** hông trong cửa sổ đang giữ. */
export function hipVisibilityRatio(
  samples: readonly DebugSample[],
  minVisibility: number,
): number {
  if (samples.length === 0) return Number.NaN;
  const visible = samples.filter(
    (s) => s.leftHipVisibility >= minVisibility && s.rightHipVisibility >= minVisibility,
  ).length;
  return visible / samples.length;
}

/** Biên độ biến thiên của một field — dùng để chấm pass/fail cho verification của DU #2. */
export function spread(samples: readonly DebugSample[], key: keyof DebugSample): number {
  const values = samples
    .map((s) => s[key])
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  if (values.length === 0) return Number.NaN;
  return Math.max(...values) - Math.min(...values);
}

/** Nhãn placement để ghi vào log — DU #2/#3 đều phải so sánh theo từng mode. */
export type DebugPlacement = CameraPlacement;
